import { isEmpty } from "lodash";

import { ColumnType } from "components/AgGrid/constants";

import { Column, LivestockSaleColId } from "constants/columns";

import { filterComparators, formatters, getters } from "lib/agGrid";
import { CommentCountColumnRenderer } from "lib/agGrid/renderers/comments";
import { SaleRenderer } from "lib/agGrid/renderers/saleRenderer";
import { caseInsensitiveCompare } from "lib/compare";
import { ColDef } from "ag-grid-community";
import {
  SaleStatusRenderer,
  SaleStatusEditor,
  SaleStatusRendererFromSalesArray,
} from "lib/agGrid/renderers/SaleStatusRenderer";

/* Note - this file contains two sets of column configuration.
 * One set is for `livestockSales` - multiple records
 * One for `livestockSale` - one record.
 * */

export const LivestockSalesColId = {
  SALE_CODES: "424c3860-6f05-44c4-9b81-4197f86dd621",
  SALE_DATES: "67cbd411-28e8-4748-833c-4f899f36ae7e",
  SALE_TITLES: "b24ae996-616c-4019-bf83-571b223301d2",
  SALE_TYPES: "e6dc8634-731f-419e-a2ec-071e47b810e6",
  SALE_STATUSES: "4308aec9-8a92-4ae8-aed5-b406a9f18727",
};

export const LivestockSalesColumnDef = {
  [LivestockSalesColId.SALE_TITLES]: {
    colId: LivestockSalesColId.SALE_TITLES,
    enableRowGroup: true,
    field: "sale_title",
    filterValueGetter: getters.firstItemInArrayGetter,
    headerName: "Name(s)",
    namespace: "livestockSales",
    valueGetter: getters.keyInObjArrayGetter,
  },
  [LivestockSalesColId.SALE_CODES]: {
    cellRenderer: SaleRenderer,
    colId: LivestockSalesColId.SALE_CODES,
    enableRowGroup: true,
    field: "sale_code",
    filterValueGetter: getters.firstItemInArrayGetter,
    headerName: "Code(s)",
    namespace: "livestockSales",
    valueGetter: getters.keyInObjArrayGetter,
  },
  [LivestockSalesColId.SALE_DATES]: {
    colId: LivestockSalesColId.SALE_DATES,
    enableRowGroup: true,
    field: "date",
    filter: "agDateColumnFilter",
    filterParams: { comparator: filterComparators.localDateComparator },
    filterValueGetter: getters.firstItemInArrayGetter,
    headerName: "Date(s)",
    namespace: "livestockSales",
    valueFormatter: params => {
      const { value } = params;
      if (Array.isArray(value)) {
        return value
          ?.map(v => formatters.localDateFormatter({ value: v }))
          .join(", ");
      }
      return formatters.localDateFormatter({ value });
    },
    valueGetter: params => {
      const values = getters.keyInObjArrayGetter(params);
      if (!values) {
        return null;
      }
      return values.map(dateStr =>
        !isEmpty(dateStr) ? new Date(dateStr) : null,
      );
    },
  },
  [LivestockSalesColId.SALE_TYPES]: {
    colId: LivestockSalesColId.SALE_TYPES,
    enableRowGroup: true,
    field: "sale_type",
    filterValueGetter: getters.firstItemInArrayGetter,
    headerName: "Type(s)",
    namespace: "livestockSales",
    valueGetter: getters.keyInObjArrayGetter,
  },
  [LivestockSalesColId.SALE_STATUSES]: {
    colId: LivestockSalesColId.SALE_STATUSES,
    headerName: "Status(es)",
    namespace: "livestockSales",
    enableRowGroup: true,
    filterValueGetter: getters.firstItemInArrayGetter,
    valueGetter: params => {
      const sales = params.data?.livestockSales;
      if (!sales?.length) return null;
      return [...new Set(sales.map(s => s.statusLabel))];
    },
    cellRenderer: SaleStatusRendererFromSalesArray,
  },
};

export const LivestockSaleColumnDefinitions: Record<string, ColDef> = {
  [LivestockSaleColId.SALE_TYPE]: {
    colId: Column.LivestockSaleColId.SALE_TYPE,
    headerName: "Sale Type",
    type: ColumnType.STRING,
    field: "livestockSale.sale_type",
    enableRowGroup: true,
  },
  [LivestockSaleColId.SALE_SUB_TYPE]: {
    colId: Column.LivestockSaleColId.SALE_SUB_TYPE,
    headerName: "Sale Sub Type",
    type: ColumnType.STRING,
    field: "livestockSale.saleSubType.name",
    enableRowGroup: true,
  },
  [LivestockSaleColId.DATE]: {
    colId: Column.LivestockSaleColId.DATE,
    headerName: "Date",
    type: ColumnType.DATE,
    field: "livestockSale.date",
    enableRowGroup: true,
  },
  [LivestockSaleColId.TITLE]: {
    colId: Column.LivestockSaleColId.TITLE,
    headerName: "Title",
    type: ColumnType.STRING,
    field: "livestockSale.sale_title",
    enableRowGroup: true,
  },
  [LivestockSaleColId.SPECIES_NAME]: {
    colId: Column.LivestockSaleColId.SPECIES_NAME,
    headerName: "Species Name",
    type: ColumnType.STRING,
    field: "livestockSale.species.name",
    enableRowGroup: true,
  },
  [LivestockSaleColId.SALE_CODE]: {
    colId: Column.LivestockSaleColId.SALE_CODE,
    headerName: "ID",
    type: ColumnType.STRING,
    field: "livestockSale.saleCode",
    comparator: caseInsensitiveCompare,
  },
  [LivestockSaleColId.SALEYARD]: {
    colId: Column.LivestockSaleColId.SALEYARD,
    headerName: "SaleYard",
    type: ColumnType.STRING,
    field: "livestockSale.saleyard.name",
    enableRowGroup: true,
  },
  [LivestockSaleColId.STATUS]: {
    colId: Column.LivestockSaleColId.STATUS,
    headerName: "Status",
    type: ColumnType.STRING,
    field: "livestockSale.status",
    enableRowGroup: true,
    cellRenderer: SaleStatusRenderer,
    cellEditor: SaleStatusEditor,
    cellEditorPopup: true,
    cellEditorPopupPosition: "over",
    editable: true,
    singleClickEdit: true,
  },
  [LivestockSaleColId.COMMENTS]: {
    colId: Column.LivestockSaleColId.COMMENTS,
    headerName: "Comments",
    type: ColumnType.STRING,
    field: "livestockSale.unResolvedCommentCount",
    width: 86,
    cellRenderer: CommentCountColumnRenderer,
    pinned: "right",
    menuTabs: [],
    filter: "agNumberColumnFilter",
  },
};
